export const UPDATE_FIELD_VALUE = 'UPDATE_FIELD_VALUE';
export const UPDATE_MULTIPLE_FIELD_VALUES = 'UPDATE_MULTIPLE_FIELD_VALUES';
export const REFRESH_FIELD_VISIBILITY = 'REFRESH_FIELD_VISIBILITY';

// Evaluate a single visibility rule condition
const evaluateCondition = (condition, fieldValues) => {
  const fieldIdStr = String(condition.field_id);
  const fieldValue = fieldValues[fieldIdStr];
  const compareValue = condition.value;

  // If fieldValue is empty, undefined, or null, handle appropriately
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return false;
  }

  switch (condition.operator) {
    case 'equal':
      return fieldValue === compareValue;
    case 'not_equal':
      return fieldValue !== compareValue;
    default:
      return false;
  }
};

// Evaluate complex visibility rules with nested conditions
const evaluateVisibilityRules = (rules, fieldValues) => {
  // If no rules at all {}, field should be visible by default
  if (!rules || (typeof rules === 'object' && Object.keys(rules).length === 0)) return true;

  // Single condition case
  if (rules.field_id) {
    return evaluateCondition(rules, fieldValues);
  }

  // Scenario with multiple conditions
  const results = rules.conditions.map((subCondition) => evaluateVisibilityRules(subCondition, fieldValues));

  return rules.type.toUpperCase() === 'AND'
    ? results.every((result) => result)
    : results.some((result) => result);
};

// Get current values map for all fields
const getCurrentFieldValues = (fields) => fields.reduce((acc, field) => {
  acc[String(field.id)] = field.current_value === undefined || field.current_value === '' ? null : field.current_value;
  return acc;
}, {});

// Fields map by stringified ID
const getFieldsMap = (fields) => new Map(fields.map((field) => [String(field.id), field]));

// Memoized function to calculate all visibilities
const calculateAllVisibilities = (currentFields, fieldValues) => {
  const fieldsMap = getFieldsMap(currentFields);
  const visibilityCache = new Map();

  // Memoized recursive function to get visibility for a single field
  const getVisibility = (fieldId) => {
    const fieldIdStr = String(fieldId); // Ensure string ID for map keys
    if (visibilityCache.has(fieldIdStr)) {
      return visibilityCache.get(fieldIdStr);
    }

    const field = fieldsMap.get(fieldIdStr);
    if (!field) {
      // eslint-disable-next-line no-console -- deliberate missing-field diagnostic
      console.warn(`Field with ID ${fieldIdStr} not found during visibility calculation.`);
      visibilityCache.set(fieldIdStr, false); // Default to not visible if field is missing
      return false;
    }

    let isVisible = true;

    // Check direct parent dependencies (field.depends_on)
    if (field.depends_on && field.depends_on.length > 0) {
      for (const parentDepId of field.depends_on) {
        if (!getVisibility(String(parentDepId))) { // Recursively get parent's visibility
          isVisible = false;
          break;
        }
      }
    }

    // If still potentially visible (all depends_on parents are visible), evaluate this field's own visibility_rules
    if (isVisible) {
      if (field.visibility_rules) {
        // Since depends_on checks passed (if any), evaluate this field's own visibility_rules
        isVisible = evaluateVisibilityRules(field.visibility_rules, fieldValues);
      } else {
        // If no specific visibility_rules for this field. Since depends_on checks passed (if any),
        // it's considered visible by default.
        isVisible = true;
      }
    }

    visibilityCache.set(fieldIdStr, Boolean(isVisible));
    return visibilityCache.get(fieldIdStr);
  };

  // Populate cache for all fields by calling getVisibility for each.
  // This ensures all visibilities are calculated, respecting dependencies.
  currentFields.forEach((field) => getVisibility(String(field.id)));

  // Return new fields array with updated 'visible' property from the cache.
  return currentFields.map((field) => ({
    ...field,
    visible: visibilityCache.get(String(field.id)) ?? false,
  }));
};

// Reducer function to handle field updates and visibility calculations
export const fieldsReducer = (fields, action) => {
  let updatedFieldsWithValue;

  switch (action.type) {
    case UPDATE_FIELD_VALUE:
      updatedFieldsWithValue = fields.map((field) => {
        if (String(field.id) !== String(action.payload.id) || field.type === 'ConstantField') {
          return field;
        }
        return { ...field, current_value: action.payload.value };
      });
      const currentFieldValues = getCurrentFieldValues(updatedFieldsWithValue);
      return calculateAllVisibilities(updatedFieldsWithValue, currentFieldValues);

    case UPDATE_MULTIPLE_FIELD_VALUES:
      return fields.map((field) => {
        if (String(field.id) !== String(action.payload.id) || field.type === 'ConstantField') {
          return field;
        }
        return { ...field, multiple_values: action.payload.values };
      });

    case REFRESH_FIELD_VISIBILITY:
      const allFieldValues = getCurrentFieldValues(fields);
      return calculateAllVisibilities(fields, allFieldValues);

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
};
